/**
 * StoragePage — flat list of a project's stores (databases / data stores).
 *
 * Mounted at /projects/:projectId/stores as a child of ProjectLayout (so
 * the sidebar / header / project context come from the layout). Just the
 * list content lives here.
 *
 * Data: GET /datasets/stores → { ok, stores: [{ storeId, name, type,
 * description, schema, status, createdAt }] }. The list is scoped to the
 * caller's project server-side, so no query params are needed.
 *
 * Deliberately a FLAT list — NO detail page and NO create form. Stores are
 * created conversationally (ask the agent / Copilot), so the empty state
 * points the user at chat rather than offering a "+ New" button.
 *
 * Visual language is a deliberate clone of AppsPage / PipelinesPage:
 *   - same `pipelines-page` wrapper + `pipelines-header*` chrome
 *   - same shared DataTable atom with column class names from the
 *     pipelines stylesheet
 *   - same status-pill colour classes (`pipelines-status-*`), so no new
 *     colour palette is introduced.
 */

import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import DataTable from '../../components/shared/DataTable/DataTable';
import MktAlertModal from '../../components/molecules/MktAlertModal/MktAlertModal';
import { Stores } from '../../services/stores';
import useProjects from '../../hooks/useProjects';
// Reuse the pipelines stylesheet for header / wrapper / column widths +
// status-pill colours so this list page looks like a sibling of Apps/Agents.
import '../PipelinesPage/PipelinesPage.css';
import './StoragePage.css';

// Map a store's raw `status` onto the shared pipelines status-pill colour
// keys so we reuse the existing palette (no new colours):
//   ready / active / available → green ("Ready")
//   provisioning / creating / pending → amber ("Provisioning")
//   error / failed → red ("Error")
//   anything else / null → gray ("Unknown")
const STATUS_KEY = (status) => {
  const s = String(status || '').toLowerCase();
  if (s === 'ready' || s === 'active' || s === 'available' || s === 'running') return 'live';
  if (s === 'provisioning' || s === 'creating' || s === 'pending' || s === 'building') return 'building';
  if (s === 'error' || s === 'failed') return 'failed';
  return 'sleeping';
};
const STATUS_LABEL = {
  live: 'Ready',
  building: 'Provisioning',
  failed: 'Error',
  sleeping: 'Unknown',
};

const StoreStatus = ({ status }) => {
  const key = STATUS_KEY(status);
  // If we got a real status string but it didn't map to a known key, show
  // the raw value (title-cased-ish) rather than a misleading "Unknown".
  const label =
    key === 'sleeping' && status ? String(status) : STATUS_LABEL[key];
  return (
    <span className={`pipelines-status pipelines-status-${key}`}>
      <span className="pipelines-status-dot" />
      {label}
    </span>
  );
};

// Compact storeId for the table: prefix up to the first `_` + the next 5 chars
// (e.g. store_cc6a8f083… → "store_cc6a8"). The full id lives in the row's
// read-only detail modal. Falls back to the first 5 chars for prefix-less ids.
const shortStoreId = (storeId) => {
  if (!storeId) return '—';
  const i = storeId.indexOf('_');
  if (i === -1) return storeId.slice(0, 5);
  return storeId.slice(0, i + 1) + storeId.slice(i + 1, i + 6);
};

// Truncate the table's DESCRIPTION cell to a fixed character budget (not a
// width-dependent CSS clip) so it reads the same on any viewport. Cuts at the
// last word boundary within the budget when possible. The FULL text stays in
// the cell's `title` attr (hover) + the row's detail modal.
const DESC_MAX = 76;
const truncateDescription = (text) => {
  const s = String(text || '').trim();
  if (s.length <= DESC_MAX) return s;
  const slice = s.slice(0, DESC_MAX);
  const lastSpace = slice.lastIndexOf(' ');
  const cut = lastSpace > DESC_MAX * 0.6 ? slice.slice(0, lastSpace) : slice;
  return `${cut.trimEnd()}…`;
};

const StoragePage = () => {
  const { projectId } = useParams();

  // The /datasets registry authenticates with the PROJECT token (it rejects
  // the user session token), so resolve the current project's apiToken from
  // the cached projects list and pass it to Stores.list().
  const { data: projects, isLoading: projectsLoading } = useProjects();
  const projectToken = projects?.find((p) => p.projectId === projectId)?.apiToken;

  const [stores, setStores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Clicked row → read-only detail modal (null = closed).
  const [selectedStore, setSelectedStore] = useState(null);

  useEffect(() => {
    // Wait for the projects list before deciding — only surface a token error
    // once we know the project actually has no token (not just still loading).
    if (projectsLoading) return;
    if (!projectToken) {
      setLoading(false);
      setError('Could not resolve this project’s token — reload the page or reopen the project.');
      setStores([]);
      return;
    }
    let alive = true;
    setLoading(true);
    setError(null);
    Stores.list(projectToken)
      .then((data) => {
        if (!alive) return;
        setStores(Array.isArray(data?.stores) ? data.stores : []);
      })
      .catch((e) => {
        if (!alive) return;
        setError(e?.message || 'Failed to load stores');
        setStores([]);
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [projectId, projectToken, projectsLoading]);

  // Columns: ID (copyable) / DESCRIPTION (the wide, primary column — what the
  // store is FOR) / TYPE (chip) / STATUS (colored dot). No NAME column: stores
  // are created conversationally and have no meaningful name (it just echoes
  // the type), so the storeId IS the identity. DESCRIPTION takes the flex
  // space; ID/TYPE/STATUS are pinned narrow. Flat list — no row actions.
  const columns = [
    {
      header: 'ID',
      key: 'storeId',
      width: '160px',
      className: 'store-col-id',
      render: (s) => <code className="store-id-short">{shortStoreId(s.storeId)}</code>,
    },
    {
      // No width → takes all remaining space (the wide, primary column).
      header: 'DESCRIPTION',
      key: 'description',
      className: 'store-col-desc',
      render: (s) =>
        s.description ? (
          <span className="store-description" title={s.description}>
            {truncateDescription(s.description)}
          </span>
        ) : (
          <span className="store-muted">—</span>
        ),
    },
    {
      header: 'TYPE',
      key: 'type',
      width: '110px',
      className: 'store-col-type',
      render: (s) =>
        s.type ? <span className="store-type-chip">{s.type}</span> : <span className="store-muted">—</span>,
    },
    {
      header: 'STATUS',
      key: 'status',
      width: '120px',
      className: 'store-col-status',
      render: (s) => <StoreStatus status={s.status} />,
    },
  ];

  return (
    <>
      <Helmet>
        <title>Storage &amp; Database · Zibby</title>
      </Helmet>

      <div className="pipelines-page">
        {(loading || stores.length > 0 || error) && (
          <div className="pipelines-header">
            <div className="pipelines-header-row">
              <h1 className="pipelines-title">Storage &amp; Database</h1>
            </div>
            <p className="pipelines-subtitle">
              Stores are created conversationally — ask your agent or the Copilot to provision one.
            </p>
          </div>
        )}

        {error && !loading ? (
          <div className="store-error" role="alert">
            Could not load stores: {error}
          </div>
        ) : (
          <DataTable
            data={stores}
            columns={columns}
            loading={loading}
            rowKey="storeId"
            onRowClick={(s) => setSelectedStore(s)}
            emptyState={{
              title: 'No stores yet',
              description:
                'No stores yet — ask your agent or the Copilot to create one.',
            }}
          />
        )}
      </div>

      {/* Read-only detail modal — opens on row click. Reuses the shared
          MktAlertModal; shows every field, full id + schema. */}
      {selectedStore && (
        <MktAlertModal
          open
          onClose={() => setSelectedStore(null)}
          title={<span className="store-modal-title">{selectedStore.storeId}</span>}
          ariaLabel="Store details"
          secondaryLabel="Close"
          maxWidth={560}
          className="store-detail-modal"
        >
          <div className="store-detail">
            <div className="store-detail-row">
              <span className="store-detail-label">Type</span>
              <span className="store-detail-value">{selectedStore.type || '—'}</span>
            </div>
            <div className="store-detail-row">
              <span className="store-detail-label">Status</span>
              <span className="store-detail-value">
                <StoreStatus status={selectedStore.status} />
              </span>
            </div>
            <div className="store-detail-row">
              <span className="store-detail-label">Created</span>
              <span className="store-detail-value">
                {selectedStore.createdAt
                  ? new Date(selectedStore.createdAt).toLocaleString()
                  : '—'}
              </span>
            </div>
            <div className="store-detail-row store-detail-row--block">
              <span className="store-detail-label">Description</span>
              <span className="store-detail-value">
                {selectedStore.description || '—'}
              </span>
            </div>
            {selectedStore.schema != null && (
              <div className="store-detail-row store-detail-row--block">
                <span className="store-detail-label">Schema</span>
                <pre className="store-detail-schema">
                  {typeof selectedStore.schema === 'string'
                    ? selectedStore.schema
                    : JSON.stringify(selectedStore.schema, null, 2)}
                </pre>
              </div>
            )}
          </div>
        </MktAlertModal>
      )}
    </>
  );
};

export default StoragePage;
